import { apiClient } from "@/api/modules/base.ts";
import { Route } from "@/api/modules/autoQ/mailLogItems/routes.ts";
import type {
    MailLogItemGroup,
    MailLogItemProduct,
    MailLogItemRegenerateQualityStatementPrefillResponse,
    MailLogItemRegenerateQualityStatementPrefillSuccess,
    MailLogItemsRequest,
    MailLogItemsResponse,
    MailLogItemUserStatistics
} from "@/api/modules/autoQ/mailLogItems/types.ts";
import type { AxiosPromise } from "axios";

export const retrieve = (payload: MailLogItemsRequest): AxiosPromise<MailLogItemsResponse> =>
    apiClient.post(Route.MailLogItems, payload);

// Scoped server-side to the authenticated user (their own outbox) - conditions.userId is set
// from the JWT and any client-sent value is ignored, so callers must not (and don't need to) send it.
export const retrieveForUser = (payload: MailLogItemsRequest): AxiosPromise<MailLogItemsResponse> =>
    apiClient.post(Route.MailLogItemsUser, payload);

export const regenerateQualityStatementPrefill = (assetId: number): AxiosPromise<MailLogItemRegenerateQualityStatementPrefillResponse> =>
    apiClient.get(Route.MailLogItemsRegenerateQualityStatementPrefill, { params: { assetId } });

// Scoped server-side to the authenticated user, same as retrieveForUser - no userId param needed.
export const retrieveStatisticsForUser = (): AxiosPromise<MailLogItemUserStatistics> =>
    apiClient.get(Route.MailLogItemsUserStatistics);

export const isRegenerateQualityStatementPrefillSuccess = (
    response: MailLogItemRegenerateQualityStatementPrefillResponse
): response is MailLogItemRegenerateQualityStatementPrefillSuccess => !("error" in response);

// LUV-2425: unwraps the grouped response (one MailLogItemGroupHit per mail, sub-items under
// innerHits.attachments) into the flatter MailLogItemGroup shape - used by outboxResultsStore
// and customerActivityStore to render one collapsible row per mail.
export const extractMailLogItemGroups = (response: MailLogItemsResponse): MailLogItemGroup[] =>
    (response.hits.hits ?? []).map((hit) => ({
        mail: hit.source,
        attachments: hit.innerHits.attachments.hits.hits.map((attachmentHit) => attachmentHit.source),
        attachmentsTotal: hit.innerHits.attachments.hits.total.value,
    }));

export const collectGroupProducts = (group: MailLogItemGroup): MailLogItemProduct[] => {
    const byKey = new Map<string, MailLogItemProduct>();

    for (const attachment of group.attachments) {
        for (const product of attachment.products) {
            const key = product.id != null ? `id:${product.id}` : `number:${product.number}`;
            if (!byKey.has(key)) byKey.set(key, product);
        }
    }

    return Array.from(byKey.values());
};